/**
 * src/services/agentPositionService.ts
 *
 * Frontend client for the GeoTrust agent-movement surface (Phase 2
 * slice 3).  All calls go through GeoTrust's backend at
 * `/api/v2/agents/...` — the browser never holds GeoLink credentials.
 *
 * Architecture recap:
 *
 *   Browser ─── REST  /api/v2/agents/movement/* ──── GeoTrust backend ──── GeoLink REST
 *   Browser ─── WS    /ws/agents                ──── GeoTrust hub     ──── GeoLink /agents/feed
 *
 * Reads are public; writes (`POST :address/position`, `:address/walk`)
 * require a Stellar Ed25519 signature from the agent itself.  The
 * browser typically only consumes reads + the WebSocket feed.  The
 * MCP server (slice 3d) authors writes using the canonical envelope
 * format defined in `agentSignatureVerifier.canonicalizePosition`.
 */

import { resolveWsUrl, safeCloseWs } from './wsBase';

// ── Shared types ────────────────────────────────────────────────────────────

export type AgentPositionSource = 'mcp' | 'walk' | 'teleport';

export interface AgentPosition {
  agent_pk:        string;
  lat:             number;
  lng:             number;
  country_code:    string | null;
  last_update_at:  string | null;
  is_stale:        boolean;
  broadcast_seq:   number | null;
}

export interface AgentListEntry {
  agent_pk:        string;
  lat:             number;
  lng:             number;
  displayName:     string | null;
  avatarUrl:       string | null;
  is_stale:        boolean;
  broadcast_seq:   number | null;
  last_update_at:  string | null;
  /**
   * ISO 3166-1 alpha-2 country code. Stamped opportunistically by
   * the marker layer (REST snapshot uses the requesting country; WS
   * frames carry their own `country_code`). May be `null` when an
   * older shape is read from local cache — consumers must tolerate
   * that. Added so the teleport HUD can show "where the agent is"
   * without a reverse-geocode round trip.
   */
  country_code?:   string | null;
}

export interface AgentNearbyResult {
  agent_pk:  string;
  center:    { lat: number; lng: number };
  radius_m:  number;
  wallets:   Array<{ wallet:      string; lat: number; lng: number; displayName?: string; distance_m: number }>;
  nfts:      Array<{ nft_id:      string; lat: number; lng: number; name?:        string; distance_m: number }>;
  contracts: Array<{ contract_id: string; lat: number; lng: number; displayName?: string; distance_m: number }>;
  agents:    Array<{ agent_pk:    string; lat: number; lng: number; displayName?: string; distance_m: number }>;
}

export interface AgentWalkResult {
  started_at:      string;
  eta:             string;
  path_distance_m: number;
  ticks_emitted:   number;
}

// WebSocket frames the hub forwards.
export type AgentFeedFrame =
  | { type: 'hello'; upstreamConfigured: boolean; at: number }
  | { type: 'pong'; at: number }
  | { type: 'upstream_state'; state: 'connected' | 'connecting' | 'disconnected' }
  | { type: 'subscribed';   feed: 'agents' | 'vehicles'; country?: string; entity_type?: string }
  | { type: 'unsubscribed'; feed: 'agents' | 'vehicles'; country?: string; entity_type?: string }
  | {
      type:           'agent_position_update';
      agent_pk:       string;
      lat:            number;
      lng:            number;
      country_code:   string;
      broadcast_seq:  number;
      displayName?:   string | null;
      avatarUrl?:     string | null;
      ts:             string;
    }
  | { type: 'agent_offline'; agent_pk: string }
  | {
      type:           'vehicle_position_update';
      entity_id:      string;
      entity_type:    string;
      lat:            number;
      lng:            number;
      altitude_m?:    number;
      country_code:   string;
      mavlink_sysid?: number;
      broadcast_seq:  number;
      ts:             string;
    }
  | { type: 'vehicle_offline'; entity_id: string };

// ── Internal ────────────────────────────────────────────────────────────────

// GeoLink migration 028 (RESOLVED 2026-05-09) widened the
// upstream agent_pk constraint to accept both classic G…
// (Ed25519) and Soroban C… (smart-account) addresses on every
// `/api/agents/*` endpoint. Mirror that here so the frontend
// guards aren't tighter than the upstream service — see
// `docs/integrations/ai-agents/geolink-c-agent-positions-2026-05-09.md`.
const STELLAR_AGENT_RE = /^[GC][A-Z2-7]{55}$/;
function isAgentPk(s: string): boolean { return STELLAR_AGENT_RE.test(s); }

async function _getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: 'application/json' } });
  const text = await r.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { raw: text }; }
  if (!r.ok) {
    const detail = (body as { error?: string; code?: string })?.error
      || (body as { code?: string })?.code
      || `HTTP ${r.status}`;
    throw new Error(`agent_position_request_failed: ${detail}`);
  }
  return body as T;
}

// ── Read endpoints ──────────────────────────────────────────────────────────

export async function getAgentPosition(agentPk: string): Promise<AgentPosition | null> {
  if (!isAgentPk(agentPk)) throw new Error('invalid_agent_pk');
  const r = await _getJson<{ success: boolean; position: AgentPosition | null }>(
    `/api/v2/agents/${encodeURIComponent(agentPk)}/position`,
  );
  return r.position ?? null;
}

export async function listAgentsByCountry(opts: {
  country: string;
  sinceSeq?: number;
  limit?: number;
  includeStale?: boolean;
} = { country: '' }): Promise<{ country: string; max_seq: number | null; agents: AgentListEntry[] }> {
  const q = new URLSearchParams({ country: (opts.country || '').toUpperCase() });
  if (Number.isFinite(opts.sinceSeq)) q.set('since_seq', String(opts.sinceSeq));
  if (Number.isFinite(opts.limit))    q.set('limit',     String(opts.limit));
  if (opts.includeStale != null)      q.set('include_stale', opts.includeStale ? 'true' : 'false');
  const r = await _getJson<{ success: boolean; country: string; max_seq: number | null; agents: AgentListEntry[] }>(
    `/api/v2/agents/movement/by-country?${q.toString()}`,
  );
  return { country: r.country, max_seq: r.max_seq, agents: r.agents || [] };
}

export async function getAgentNearby(agentPk: string, opts: {
  radius_m?: number;
  kinds?: Array<'wallets' | 'nfts' | 'contracts' | 'agents'>;
} = {}): Promise<AgentNearbyResult> {
  if (!isAgentPk(agentPk)) throw new Error('invalid_agent_pk');
  const q = new URLSearchParams();
  if (Number.isFinite(opts.radius_m)) q.set('radius_m', String(opts.radius_m));
  if (opts.kinds && opts.kinds.length) q.set('kinds', opts.kinds.join(','));
  const path = `/api/v2/agents/${encodeURIComponent(agentPk)}/nearby` +
               (q.toString() ? `?${q.toString()}` : '');
  return await _getJson<AgentNearbyResult>(path);
}

export async function getMovementHealth(): Promise<{ configured: boolean; canWrite: boolean }> {
  const r = await _getJson<{ success: boolean; configured: boolean; canWrite: boolean }>(
    `/api/v2/agents/movement/health`,
  );
  return { configured: !!r.configured, canWrite: !!r.canWrite };
}

// ── Write endpoints (signed payloads) ───────────────────────────────────────
//
// The frontend rarely calls these directly — agent ownership of the
// Ed25519 secret key lives in the MCP server.  Exposed for completeness
// (admin tooling, tests).

export interface SignedPositionRequest {
  payload: {
    agent:    string;
    ts:       number;
    nonce:    string;
    lat:      number;
    lng:      number;
    country:  string;
    source:   AgentPositionSource;
    sequence?: number;
    ledger?:  number;
  };
  signature: string; // base64-encoded Ed25519 signature
}

export async function submitAgentPosition(agentPk: string, signed: SignedPositionRequest): Promise<unknown> {
  if (!isAgentPk(agentPk)) throw new Error('invalid_agent_pk');
  const r = await fetch(`/api/v2/agents/${encodeURIComponent(agentPk)}/position`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body:    JSON.stringify(signed),
  });
  const text = await r.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { raw: text }; }
  if (!r.ok) {
    const e = body as { error?: string; code?: string; retry_after_ms?: number };
    throw new Error(`submit_position_failed:${e?.code || e?.error || r.status}${e?.retry_after_ms ? `:retry_after_ms=${e.retry_after_ms}` : ''}`);
  }
  return body;
}

export async function submitAgentWalk(agentPk: string, signed: SignedPositionRequest): Promise<AgentWalkResult> {
  if (!isAgentPk(agentPk)) throw new Error('invalid_agent_pk');
  const r = await fetch(`/api/v2/agents/${encodeURIComponent(agentPk)}/walk`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body:    JSON.stringify(signed),
  });
  const text = await r.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { raw: text }; }
  if (!r.ok) {
    const e = body as { error?: string; code?: string };
    throw new Error(`submit_walk_failed:${e?.code || e?.error || r.status}`);
  }
  return body as AgentWalkResult;
}

// ── WebSocket subscription ──────────────────────────────────────────────────

export interface AgentFeedSubscription {
  feed:        'agents' | 'vehicles';
  country?:    string;
  entity_type?: string;
}

export interface AgentFeedHandle {
  /** Update the active subscriptions (server is told to subscribe / unsubscribe as needed). */
  setSubscriptions(subs: AgentFeedSubscription[]): void;
  close(): void;
  /** Current upstream state forwarded by the hub. */
  upstreamState(): 'connected' | 'connecting' | 'disconnected';
}

/**
 * Open a WebSocket against `/ws/agents`, with auto-reconnect.
 * `onFrame` is called for every server frame so callers can either
 * react to specific event types or build a debug feed.
 */
export function openAgentFeed(opts: {
  initialSubs?: AgentFeedSubscription[];
  onFrame:      (frame: AgentFeedFrame) => void;
  onError?:     (err: Event | unknown) => void;
}): AgentFeedHandle {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let upstream: 'connected' | 'connecting' | 'disconnected' = 'disconnected';
  let subs: AgentFeedSubscription[] = opts.initialSubs ? [...opts.initialSubs] : [];
  const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

  const url = resolveWsUrl('/ws/agents');

  const send = (m: object) => { try { ws?.send(JSON.stringify(m)); } catch { /* swallow */ } };

  const sendSubs = () => {
    for (const s of subs) {
      const msg: { type: string; feed: string; country?: string; entity_type?: string } = { type: 'subscribe', feed: s.feed };
      if (s.country)     msg.country     = s.country;
      if (s.entity_type) msg.entity_type = s.entity_type;
      send(msg);
    }
  };

  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      opts.onError?.(err);
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      retry = 0;
      sendSubs();
    };
    ws.onmessage = (evt) => {
      let frame: AgentFeedFrame | null = null;
      try { frame = JSON.parse(String(evt.data)); } catch { return; }
      if (!frame) return;
      if (frame.type === 'upstream_state') upstream = frame.state;
      try { opts.onFrame(frame); } catch { /* user handler error */ }
    };
    ws.onerror = (err) => { opts.onError?.(err); };
    ws.onclose = () => {
      ws = null;
      if (!closed) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const delay = RETRY_MS[Math.min(retry, RETRY_MS.length - 1)];
    retry += 1;
    setTimeout(connect, delay);
  };

  connect();

  return {
    setSubscriptions(next: AgentFeedSubscription[]): void {
      // Diff: compute additions / removals against the current set.
      const key = (s: AgentFeedSubscription) => `${s.feed}:${s.country || ''}:${s.entity_type || ''}`;
      const beforeKeys = new Set(subs.map(key));
      const afterKeys  = new Set(next.map(key));
      // Unsubscribe what's gone.
      for (const s of subs) {
        if (!afterKeys.has(key(s))) {
          const msg: { type: string; feed: string; country?: string; entity_type?: string } = { type: 'unsubscribe', feed: s.feed };
          if (s.country)     msg.country     = s.country;
          if (s.entity_type) msg.entity_type = s.entity_type;
          send(msg);
        }
      }
      // Subscribe what's new.
      for (const s of next) {
        if (!beforeKeys.has(key(s))) {
          const msg: { type: string; feed: string; country?: string; entity_type?: string } = { type: 'subscribe', feed: s.feed };
          if (s.country)     msg.country     = s.country;
          if (s.entity_type) msg.entity_type = s.entity_type;
          send(msg);
        }
      }
      subs = [...next];
    },
    close(): void {
      closed = true;
      safeCloseWs(ws);
      ws = null;
    },
    upstreamState(): 'connected' | 'connecting' | 'disconnected' {
      return upstream;
    },
  };
}
